import React, { createContext, useContext, useState, useCallback, ReactNode, useEffect, useRef, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { getSession } from '@/lib/supabaseClient';
import { AuthEvents } from '@/utils/events';
import { logger } from '@/utils/logger';
import { generateUUID } from '@/utils/uuidUtils';

// API URL
const serverUrl = import.meta.env.VITE_BACKEND_SERVER_URL
const API_URL = `${serverUrl}/v1`;

// Types
export interface Conversation {
  id: string;
  title: string;
  createdAt: string;
  updatedAt: string;
  lastMessage?: string;
  messageCount: number;
}

export interface FileAttachment {
  file_id: string;
  filename: string;
  content_type: string;
  size: number;
  url: string;
  uploaded_at: string;
}

export interface Message {
  id: string;
  conversationId: string;
  content: string;
  role: 'user' | 'assistant';
  timestamp: string;
  files?: FileAttachment[];
  model?: string;
  meta?: {
    usage?: any;
    analysis?: any; // Contains the analysis data from the backend
  };
}

export interface FrontendFile {
  id: string;
  name: string;
  contentType: string;
  size: number;
  url: string;
  file?: File; // Original file object for upload
}

interface UsageStats {
  messagesRemaining: number;
  tokensUsed: number;
  resetDate: string;
  rateLimited?: boolean;
  rateLimitMessage?: string;
  limitType?: string;
  upgradeUrl?: string;
}

interface ChatContextType {
  conversations: Conversation[];
  currentConversation: Conversation | null;
  messages: Message[];
  isLoading: boolean;
  usageStats: UsageStats | null;
  followupSuggestions: string[];
  createConversation: (title?: string) => Promise<Conversation | null>;
  loadConversation: (conversationId: string) => Promise<void>;
  loadAllConversations: () => Promise<void>;
  sendMessage: (content: string, files?: FrontendFile[], conversationId?: string) => Promise<void>;
  deleteConversation: (conversationId: string) => Promise<void>;
  updateConversation: (conversationId: string, title: string) => Promise<void>;
  uploadFile: (file: File) => Promise<FrontendFile | null>;
  resetChatState: () => void;
}

// Create the context
export const ChatContext = createContext<ChatContextType | undefined>(undefined);

// Custom hook to use the context
export const useChat = (): ChatContextType => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
};

interface ChatProviderProps {
  children: ReactNode;
}

export const ChatProvider: React.FC<ChatProviderProps> = ({ children }) => {
  const { userProfile, sessionId, anonymousToken, authVersion } = useAuth();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [currentConversation, setCurrentConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [usageStats, setUsageStats] = useState<{
    messagesRemaining: number;
    tokensUsed: number;
    resetDate: string;
  } | null>(null);
  const [followupSuggestions, setFollowupSuggestions] = useState<string[]>([]);
  
  // Add refs for tracking API calls and visibility state
  const conversationsLoadedRef = useRef<boolean>(false);
  const isVisibleRef = useRef<boolean>(true);
  const lastLoadTimeRef = useRef<number>(0);

  // Helper to get auth headers
  const getHeadersForApi = useCallback(async (includeContentType = true) => {
    if (!userProfile) {
      logger.debug('ChatContext', 'Skipping API request - no user profile');
      throw new Error('User not authenticated');
    }
    
    const headers: Record<string, string> = {};
    
    if (includeContentType) {
      headers['Content-Type'] = 'application/json';
    }

    // Centralized token handling for anonymous users
    if (userProfile?.isAnonymous) {
      // First try to get token from localStorage
      let token = localStorage.getItem('anonymous_token');
      
      // If token exists in state but not in localStorage, use state token
      if (!token && anonymousToken) {
        token = anonymousToken;
        // Store it in localStorage for persistence
        localStorage.setItem('anonymous_token', anonymousToken);
      }
      
      // If we have a valid token, use it
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      } 
      // Otherwise, fetch a new token if we have a sessionId
      else if (sessionId) {
        try {
          console.log(`${API_URL}/auth/anonymous`)
          const response = await fetch(`${API_URL}/auth/anonymous`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Anonymous-Session': sessionId
            },
            body: JSON.stringify({ sessionId })
          });
          
          if (response.ok) {
            const data = await response.json();
            if (data.status === 'success' && data.data?.token) {
              // Ensure token is a non-null string before using it
              const newToken = data.data.token as string;
              localStorage.setItem('anonymous_token', newToken);
              headers['Authorization'] = `Bearer ${newToken}`;
            } else {
              // Fallback to session ID if token fetch fails
              if (sessionId) {
                headers['X-Anonymous-Session'] = sessionId;
              }
            }
          } else {
            // Fallback to session ID if request fails
            if (sessionId) {
              headers['X-Anonymous-Session'] = sessionId;
            }
          }
        } catch (error) {
          // Fallback to session ID if request throws
          if (sessionId) {
            headers['X-Anonymous-Session'] = sessionId;
          }
        }
      }
    } else {
      // Authenticated user path
      try {
        const { session } = await getSession();
        const token = session?.access_token;
        
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      } catch (error) {
        logger.error('ChatContext', 'Error getting authenticated session:', error);
        // No fallback for authenticated users if session fetch fails
      }
    }
    
    return headers;
  }, [userProfile?.isAnonymous, sessionId, anonymousToken]);

  const loadAllConversationsFromApi = useCallback(async () => {
    // If there's no user profile, don't try to load conversations
    if (!userProfile) {
      logger.debug('ChatContext', 'Skipping conversation loading - no user profile');
      return;
    }
    
    // Add debounce to prevent too many API calls
    const now = Date.now();
    const timeSinceLastLoad = now - lastLoadTimeRef.current;
    
    // Skip if we've loaded within the last 5 seconds (unless it's a forced reload)
    if (conversationsLoadedRef.current && timeSinceLastLoad < 5000) {
      logger.debug('ChatContext', 'Skipping conversation loading - recently loaded');
      return;
    }
    
    // Update last load time
    lastLoadTimeRef.current = now;
    
    try {
      logger.debug('ChatContext', 'Loading conversations from API');
      setIsLoading(true);
      const headers = await getHeadersForApi();

      const response = await fetch(`${API_URL}/conversations`, {
        headers
      });

      if (!response.ok) {
        logger.error('ChatContext', `Failed to fetch conversations: ${response.status}`);
        throw new Error(`Failed to fetch conversations: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.status === 'success' && data.data?.conversations) {
        // Transform API response to match our interface
        const conversationsData = data.data.conversations.map((conv: any) => ({
          id: conv.id,
          title: conv.title,
          createdAt: conv.createdAt,
          updatedAt: conv.updatedAt,
          lastMessage: conv.lastMessage,
          messageCount: conv.messageCount
        }));
        
        logger.debug('ChatContext', `Loaded ${conversationsData.length} conversations`);
        setConversations(conversationsData);
        
        // Mark as loaded
        conversationsLoadedRef.current = true;
      } else {
        logger.debug('ChatContext', 'No conversations found or invalid response format');
        setConversations([]);
      }
    } catch (error) {
      logger.error('ChatContext', 'Error loading conversations:', error);
      setConversations([]);
    } finally {
      setIsLoading(false);
    }
  }, [getHeadersForApi, !!userProfile]);

  const createNewConversation = useCallback(async (title = 'New Conversation') => {
    try {
      logger.debug('ChatContext', `Creating new conversation with title: ${title}`);
      setIsLoading(true);
      const headers = await getHeadersForApi();

      logger.debug('ChatContext', 'Creating conversation with headers:', headers);
      const response = await fetch(`${API_URL}/conversations`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ title })
      });

      if (!response.ok) {
        logger.error('ChatContext', `Failed to create conversation: ${response.status}`);
        throw new Error(`Failed to create conversation: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.status === 'success' && data.data?.conversation) {
        const newConversation: Conversation = {
          id: data.data.conversation.id,
          title: data.data.conversation.title,
          createdAt: data.data.conversation.createdAt,
          updatedAt: data.data.conversation.updatedAt,
          messageCount: data.data.conversation.messageCount
        };
        
        logger.debug('ChatContext', 'Conversation created successfully:', newConversation);
        setConversations(prev => [newConversation, ...prev]);
        setCurrentConversation(newConversation);
        setMessages([]);
        
        return newConversation;
      }
      
      logger.error('ChatContext', 'Failed to create conversation - invalid response format');
      return null;
    } catch (error) {
      logger.error('ChatContext', 'Error creating conversation:', error);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [getHeadersForApi]);

  const loadConversationById = useCallback(async (conversationId: string) => {
    try {
      logger.debug('ChatContext', `Loading conversation by ID: ${conversationId}`);
      setIsLoading(true);
      const headers = await getHeadersForApi();
      
      // Fetch conversation details
      const convResponse = await fetch(`${API_URL}/conversations/${conversationId}`, {
        headers
      });
      
      if (!convResponse.ok) {
        logger.error('ChatContext', `Failed to fetch conversation: ${convResponse.status}`);
        throw new Error(`Failed to fetch conversation: ${convResponse.status}`);
      }
      
      const convData = await convResponse.json();
      
      if (convData.status === 'success' && convData.data?.conversation) {
        const conversation: Conversation = {
          id: convData.data.conversation.id,
          title: convData.data.conversation.title,
          createdAt: convData.data.conversation.createdAt,
          updatedAt: convData.data.conversation.updatedAt,
          messageCount: convData.data.conversation.messageCount
        };
        
        logger.debug('ChatContext', 'Conversation details loaded:', conversation);
        setCurrentConversation(conversation);
        
        // Fetch messages for this conversation
        logger.debug('ChatContext', `Loading messages for conversation: ${conversationId}`);
        const msgResponse = await fetch(`${API_URL}/conversations/${conversationId}/messages`, {
          headers
        });
        
        if (!msgResponse.ok) {
          logger.error('ChatContext', `Failed to fetch messages: ${msgResponse.status}`);
          throw new Error(`Failed to fetch messages: ${msgResponse.status}`);
        }
        
        const msgData = await msgResponse.json();
        
        if (msgData.status === 'success' && msgData.data?.messages) {
          const messagesData = msgData.data.messages.map((msg: any) => ({
            id: msg.id,
            conversationId: msg.conversationId,
            content: msg.content,
            role: msg.role,
            timestamp: msg.timestamp,
            files: msg.files,
            model: msg.model, // Include model field
            meta: {
              // Only include analysis in development mode
              analysis: import.meta.env.VITE_ENVIRONMENT === 'development' ? 
                msgData.meta?.analysis?.[msg.id] || null : null
            }
          }));
          
          logger.debug('ChatContext', `Loaded ${messagesData.length} messages`);
          setMessages(messagesData);
        } else {
          logger.debug('ChatContext', 'No messages found or invalid response format');
          setMessages([]);
        }
      }
    } catch (error) {
      logger.error('ChatContext', 'Error loading conversation:', error);
      setMessages([]);getHeadersForApi
    } finally {
      setIsLoading(false);
    }
  }, [getHeadersForApi]);

  const updateConversationInApi = useCallback(async (conversationId: string, title: string) => {
    try {
      logger.debug('ChatContext', `Updating conversation ${conversationId} with title: ${title}`);
      setIsLoading(true);
      const headers = await getHeadersForApi();
      
      logger.debug('ChatContext', `Updating conversation ${conversationId} with headers:`, headers);
      const response = await fetch(`${API_URL}/conversations/${conversationId}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ title })
      });
      
      if (!response.ok) {
        logger.error('ChatContext', `Failed to update conversation: ${response.status}`);
        throw new Error(`Failed to update conversation: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.status === 'success' && data.data?.conversation) {
        const updatedConversation: Conversation = {
          id: data.data.conversation.id,
          title: data.data.conversation.title,
          createdAt: data.data.conversation.createdAt,
          updatedAt: data.data.conversation.updatedAt,
          messageCount: data.data.conversation.messageCount
        };
        
        logger.debug('ChatContext', 'Conversation updated successfully:', updatedConversation);
        
        // Update in conversations list
        setConversations(prev => 
          prev.map(conv => 
            conv.id === conversationId ? updatedConversation : conv
          )
        );
        
        // Update current conversation if it's the one we're viewing
        if (currentConversation?.id === conversationId) {
          setCurrentConversation(updatedConversation);
        }
      }
    } catch (error) {
      logger.error('ChatContext', 'Error updating conversation:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [currentConversation, getHeadersForApi]);

  const uploadFileToApi = useCallback(async (file: File): Promise<FrontendFile | null> => {
    try {
      logger.debug('ChatContext', `Uploading file: ${file.name} (${file.size} bytes)`);
      const headers = await getHeadersForApi(false); // Don't include Content-Type for file uploads
      
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('file', file);
      
      logger.debug('ChatContext', 'Uploading file with headers:', headers);
      const response = await fetch(`${API_URL}/files/upload`, {
        method: 'POST',
        headers,
        body: formData
      });
      
      if (!response.ok) {
        logger.error('ChatContext', `Failed to upload file: ${response.status}`);
        throw new Error(`Failed to upload file: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.status === 'success' && data.data?.file) {
        logger.debug('ChatContext', 'File uploaded successfully:', data.data.file);
        return {
          id: data.data.file.id,
          name: data.data.file.name,
          contentType: data.data.file.contentType,
          size: data.data.file.size,
          url: data.data.file.url
        };
      }
      
      logger.error('ChatContext', 'Failed to upload file - invalid response format');
      return null;
    } catch (error) {
      logger.error('ChatContext', 'Error uploading file:', error);
      return null;
    }
  }, [getHeadersForApi]);

  const sendMessageToApi = useCallback(async (content: string, files: FrontendFile[] = [], specificConversationId?: string) => {
    logger.info('ChatContext', "sendMessageToApi called");
    
    // If a specific conversation ID is provided, use that instead of the current conversation
    let activeConversation = currentConversation;
    let conversationId = specificConversationId;
    
    if (specificConversationId) {
      // If we have a specific ID, check if it's a conversation we already know
      const existingConv = conversations.find(c => c.id === specificConversationId);
      
      if (existingConv) {
        activeConversation = existingConv;
      } else {
        // Create a temporary conversation object
        activeConversation = {
          id: specificConversationId,
          title: 'New Conversation',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          messageCount: 0
        };
      }
    } else if (!activeConversation) {
      // This is the existing code for when no conversation exists and none is specified
      logger.debug('ChatContext', 'No active conversation, generating UUID for new conversation');
      try {
        // Generate a new UUID for the conversation
        conversationId = generateUUID();
        
        // Create a temporary conversation object
        const newConversation: Conversation = {
          id: conversationId,
          title: 'New Conversation',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          messageCount: 0
        };
        
        activeConversation = newConversation;
        
        // Set as current conversation to avoid multiple creations
        setCurrentConversation(newConversation);
        
        // Add to conversations list (will be updated once we get real data from API)
        setConversations(prev => [...prev, newConversation]);
        
        // Update URL to include the new conversation ID
        window.history.pushState({}, '', `/chat/${conversationId}`);
      } catch (error) {
        logger.error('ChatContext', 'Error generating UUID for new conversation:', error);
        throw error;
      }
    }
    
    // Create optimistic update for user message
    const tempUserMsgId = `temp-${Date.now()}`;
    const userMessage: Message = {
      id: tempUserMsgId,
      conversationId: activeConversation.id,
      content,
      role: 'user',
      timestamp: new Date().toISOString(),
      files: files.map(file => ({
        file_id: file.id,
        filename: file.name,
        content_type: file.contentType,
        size: file.size,
        url: file.url,
        uploaded_at: new Date().toISOString()
      }))
    };
  
    logger.debug('ChatContext', 'Adding optimistic user message to UI');
    // Add user message to UI immediately
    setMessages(prev => [...prev, userMessage]);
  
    try {
      logger.debug('ChatContext', `Sending message to conversation ${activeConversation.id}`);
      setIsLoading(true);
      const headers = await getHeadersForApi();
      
      // Format files for API
      const fileIds = files.map(file => file.id);
      
      logger.debug('ChatContext', `Sending message to conversation ${activeConversation.id} with headers:`, headers);
      // Send message to API
      const response = await fetch(`${API_URL}/conversations/${activeConversation.id}/messages`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ 
          content, 
          fileIds 
        }),
      });
  
      // Always parse the response JSON, regardless of status code
      const data = await response.json();
      logger.debug('ChatContext', 'Response from message API:', data);
  
      if (!response.ok) {
        // Handle rate limit errors (429)
        if (response.status === 429) {
          logger.debug('ChatContext', 'Rate limit exceeded:', data);
          
          // Extract rate limit information from the response
          // Check if there are error details in the expected format
          const errors = data.errors || [];
          const rateLimit = errors.find((err: any) => err.code === 'rate_limit');
          
          if (rateLimit) {
            const limitType = rateLimit.meta?.limitType || 'message';
            const resetTime = rateLimit.meta?.resetTime;
            const upgradeUrl = rateLimit.meta?.upgradeUrl;
            
            logger.debug('ChatContext', 'Rate limit details:', { 
              limitType, 
              resetTime, 
              upgradeUrl, 
              message: rateLimit.message 
            });
            
            // Update usage stats with rate limit information
            setUsageStats({
              messagesRemaining: 0,
              tokensUsed: usageStats?.tokensUsed || 0,
              resetDate: resetTime || new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString()
            });
            
            // Keep the user message, don't clear loading as we've handled it
            setIsLoading(false);
            
            // Throw a special error that the UI can handle differently
            throw new Error(`RATE_LIMIT:${limitType}:${rateLimit.message}`);
          } else {
            // If we got a 429 but couldn't extract the structured error info,
            // still try to provide a fallback rate limit warning
            logger.error('ChatContext', 'Received 429 but no structured error data found');
            
            setUsageStats({
              messagesRemaining: 0,
              tokensUsed: usageStats?.tokensUsed || 0,
              resetDate: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
            });
            
            setIsLoading(false);
            throw new Error(`RATE_LIMIT:unknown:${data.message || 'Rate limit exceeded'}`);
          }
        }
        
        // Handle other errors
        logger.error('ChatContext', 'Error response from message API:', data);
        
        // If this was a new conversation that we just created, clean it up
        if (!currentConversation || currentConversation.id !== activeConversation.id) {
          setCurrentConversation(null);
          setConversations(prev => prev.filter(conv => conv.id !== activeConversation.id));
        }
        
        throw new Error(`Failed to send message: ${response.status} - ${data.message || 'Unknown error'}`);
      }
      
      if (data.status === 'success') {
        logger.debug('ChatContext', 'Message sent successfully, processing response');
        // Replace optimistic user message and add assistant response
        const apiMessages = data.data.messages.map((msg: any) => ({
          id: msg.id,
          conversationId: msg.conversationId,
          content: msg.content,
          role: msg.role,
          timestamp: msg.timestamp,
          files: msg.files || [],
          model: msg.model || null, // Add the model field from the API response
          meta: {
            // Only include analysis in development mode
            analysis: import.meta.env.VITE_ENVIRONMENT === 'development' ? 
              data.meta?.analysis || null : null,
            usage: import.meta.env.VITE_ENVIRONMENT === 'development' ? 
              data.meta?.usage || null : null
          }
        }));
        
        setMessages(prev => 
          prev.filter(msg => msg.id !== tempUserMsgId).concat(apiMessages)
        );
        
        // Update usage stats if available
        if (data.meta?.usage) {
          logger.debug('ChatContext', 'Updating usage stats:', data.meta.usage);
          setUsageStats({
            messagesRemaining: data.meta.usage.messagesRemaining,
            tokensUsed: data.meta.usage.tokensUsed,
            resetDate: data.meta.usage.resetDate
          });
        }
        
        // Update followup suggestions if available
        if (data.meta?.followupSuggestions) {
          logger.debug('ChatContext', 'Setting followup suggestions');
          setFollowupSuggestions(data.meta.followupSuggestions);
        } else {
          setFollowupSuggestions([]);
        }
        
        // Check if conversation title was updated (for new conversations)
        const newTitle = data.meta?.conversationTitle;
        
        // Create an updated conversation object
        const updatedConversation = {
          ...activeConversation,
          title: newTitle || activeConversation.title,
          updatedAt: new Date().toISOString(),
          messageCount: activeConversation.messageCount + 2, // User + assistant messages
          lastMessage: apiMessages[apiMessages.length - 1].content
        };
        
        setCurrentConversation(updatedConversation);
        
        // Update in conversations list
        setConversations(prev => {
          // Check if the conversation exists in the list
          const exists = prev.some(conv => conv.id === activeConversation.id);
          
          if (exists) {
            // Update existing conversation
            return prev.map(conv => 
              conv.id === activeConversation.id ? updatedConversation : conv
            );
          } else {
            // Add new conversation to the list
            return [...prev, updatedConversation];
          }
        });
      }
    } catch (error) {
      logger.error('ChatContext', 'Error sending message:', error);
      
      // Check if this is a rate limit error that we've already handled
      if (error instanceof Error && error.message.startsWith('RATE_LIMIT:')) {
        // Keep the user message in this case, but don't add an additional error message
        return;
      }
      
      // Remove the optimistic message on error
      logger.debug('ChatContext', 'Removing optimistic message due to error');
      setMessages(prev => prev.filter(msg => msg.id !== tempUserMsgId));
      
      // If this was a new conversation that we just created, clean it up on error
      if (!currentConversation || currentConversation.id !== activeConversation.id) {
        setCurrentConversation(null);
        setConversations(prev => prev.filter(conv => conv.id !== activeConversation.id));
      }
      
      throw error;
    } finally {
      // Make sure loading state is cleared
      setIsLoading(false);
    }
  }, [currentConversation, getHeadersForApi, usageStats]);
  
  const deleteConversationFromApi = useCallback(async (conversationId: string) => {
    try {
      logger.debug('ChatContext', `Deleting conversation ${conversationId}`);
      setIsLoading(true);
      const headers = await getHeadersForApi();
      
      logger.debug('ChatContext', `Deleting conversation ${conversationId} with headers:`, headers);
      const response = await fetch(`${API_URL}/conversations/${conversationId}`, {
        method: 'DELETE',
        headers
      });
      
      if (!response.ok) {
        logger.error('ChatContext', `Failed to delete conversation: ${response.status}`);
        throw new Error(`Failed to delete conversation: ${response.status}`);
      }
      
      logger.debug('ChatContext', 'Conversation deleted successfully');
      
      // Remove from conversations list
      setConversations(prev => prev.filter(conv => conv.id !== conversationId));
      
      // If we were viewing this conversation, clear current state
      if (currentConversation?.id === conversationId) {
        setCurrentConversation(null);
        setMessages([]);
      }
    } catch (error) {
      logger.error('ChatContext', 'Error deleting conversation:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [currentConversation, getHeadersForApi]);

  // Function to reset all chat state - useful for logout
  const resetChatState = useCallback(() => {
    logger.info('ChatContext', 'Resetting chat state due to logout');
    setConversations([]);
    setCurrentConversation(null);
    setMessages([]);
    setFollowupSuggestions([]);

    conversationsLoadedRef.current = false;
    // We don't reset usage stats since they might still be useful on the login page
  }, []);

  // Handle document visibility changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      const wasVisible = isVisibleRef.current;
      isVisibleRef.current = document.visibilityState === 'visible';
      
      // Only trigger load if we become visible AND we were previously hidden
      if (isVisibleRef.current && !wasVisible) {
        // Don't immediately reload - wait a moment and only reload if needed
        setTimeout(() => {
          const now = Date.now();
          const timeSinceLastLoad = now - lastLoadTimeRef.current;
          
          // Only reload if it's been more than 2 minutes since last load
          if (timeSinceLastLoad > 120000) {
            logger.debug('ChatContext', 'Triggering reload after visibility change');
            loadAllConversationsFromApi(); // Force reload
          }
        }, 1000);
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [loadAllConversationsFromApi]);

  // Listen for auth events - particularly logout
  useEffect(() => {
    // Subscribe to the BEFORE_LOGOUT event
    logger.debug('ChatContext', 'Setting up BEFORE_LOGOUT event listener');
    
    const unsubscribeBeforeLogout = AuthEvents.BEFORE_LOGOUT.subscribe(() => {
      logger.info('ChatContext', 'Received BEFORE_LOGOUT event, resetting chat state');
      resetChatState();
    });
    
    // Clean up the subscription when the component unmounts
    return () => {
      logger.debug('ChatContext', 'Cleaning up BEFORE_LOGOUT event listener');
      unsubscribeBeforeLogout();
    };
  }, [resetChatState]);

  useEffect(() => {
    logger.debug('ChatContext', 'Effect: userProfile changed:', userProfile);
    if (!userProfile) {
      logger.debug('ChatContext', 'No userProfile. Skipping conversation load.');
      return;
    }
  
    if (!conversationsLoadedRef.current) {
      logger.debug('ChatContext', 'Loading conversations from API...');
      loadAllConversationsFromApi();
    }
  }, [authVersion]);

  useEffect(() => {
    if (
      conversationsLoadedRef.current &&
      conversations.length > 0 &&
      !currentConversation &&
      userProfile
    ) {
      const sorted = [...conversations].sort((a, b) =>
        new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
      );
      const mostRecent = sorted[0];
      logger.debug('ChatContext', 'Auto-selecting most recent conversation:', mostRecent.id);
  
      loadConversationById(mostRecent.id);
      window.history.pushState({}, '', `/chat/${mostRecent.id}`);
    }
  }, [conversations, currentConversation, userProfile, loadConversationById]);
  
  // Update usage stats from user profile if available
  useEffect(() => {
    if (userProfile?.usage) {
      logger.debug('ChatContext', 'Updating usage stats from user profile');
      setUsageStats({
        messagesRemaining: userProfile.usage.messagesRemaining,
        tokensUsed: userProfile.usage.tokenUsage,
        resetDate: userProfile.usage.resetDate
      });
    }
  }, [userProfile]);

  // Create the context value with public-facing function names
  const value = useMemo(() => ({
    conversations,
    currentConversation,
    messages,
    isLoading,
    usageStats,
    followupSuggestions,
    createConversation: createNewConversation,
    loadConversation: loadConversationById,
    loadAllConversations: loadAllConversationsFromApi,
    sendMessage: (content: string, files: FrontendFile[] = [], conversationId?: string) => 
      sendMessageToApi(content, files, conversationId),
    deleteConversation: deleteConversationFromApi,
    updateConversation: updateConversationInApi,
    uploadFile: uploadFileToApi,
    resetChatState
  }), [
    conversations,
    currentConversation,
    messages,
    isLoading, 
    usageStats,
    followupSuggestions,
    createNewConversation,
    loadConversationById,
    loadAllConversationsFromApi,
    sendMessageToApi,
    deleteConversationFromApi,
    updateConversationInApi,
    uploadFileToApi,
    resetChatState
  ]);

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};
